import React, { useCallback, useState, useMemo } from "react";
import styled, { css } from "styled-components";
import useLayoutVariants from "components/common/PaginatedEntityTable/hooks/useLayoutVariants";
import {
  ALERTS_LAYOUTS_VARIANTS,
  SECURITY_EVENTS_ENTITY_TABLE_ID
} from "security-app/components/SecurityEvents/Alerts/Constants";
import { IconButton, Spinner } from "components/common";
import PredefinedLayoutItem from "security-app/components/SecurityEvents/Alerts/AlertsEntityTableTopSection/PredefinedLayoutItem";
import { SegmentedControl } from "components/bootstrap";
import useLayoutVariant from "components/common/PaginatedEntityTable/hooks/useLayoutVariant";
import { useTableFilterContext } from "components/common/PaginatedEntityTable";
import useTableFetchContext from "components/common/PaginatedEntityTable/useTableFetchContext";
import useUpdateUserLayoutPreferences from "components/common/EntityDataTable/hooks/useUpdateUserLayoutPreferences";
import useUserLayoutPreferences from "components/common/EntityDataTable/hooks/useUserLayoutPreferences";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { defaultTimeRange } from "components/events/fetchEvents";
const SectionContainer = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.spacings.sm};
  `
);
const ContentRow = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.spacings.sm};
    width: 100%;
  `
);
const Content = styled.div`
  flex: 1;
  min-width: 0;
`;
const ToggleContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const Container = styled.div(
  ({ theme }) => css`
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: ${theme.spacings.sm};
    width: 100%;
  `
);
const SwitcherContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacings.xs};
  `
);
const layoutVariantDescriptions = (layout) => {
  switch (layout) {
    case ALERTS_LAYOUTS_VARIANTS.INVESTIGATIONS:
      return "Switching to this layout, sorts by Risk Score from highest to lowest, slices by Associated Investigations, and orders investigation slices by Priority from highest to lowest. You can configure any part of table layout by yourself except slicing.";
    case ALERTS_LAYOUTS_VARIANTS.ASSETS:
      return "Switching to this layout, sorts by Risk Score from highest to lowest, slices by Associated Assets, and orders asset slices by Risk Score from highest to lowest. You can configure any part of table layout by yourself except slicing.";
    case ALERTS_LAYOUTS_VARIANTS.SYSTEM_ALERTS:
      return "Switching to this layout filters to system notification events, and sorts by Timestamp from newest to oldest. You can configure any part of table layout by yourself.";
    case ALERTS_LAYOUTS_VARIANTS.ALERTS:
      return "Switching to this layout shows alerts and sorts by Timestamp from newest to oldest. You can configure any part of table layout by yourself.";
    default:
      return "Switching to this layout shows all alerts and events with your own layout preferences. You can configure any part of table layout by yourself.";
  }
};
const AlertsEntityTableLayoutSwitcher = ({ layoutVariants }) => {
  const { activeLayoutVariant, selectLayoutVariant } = useLayoutVariant();
  const { resetFilters } = useTableFilterContext();
  const onChange = (layoutVariant) => {
    resetFilters();
    selectLayoutVariant(layoutVariant === "default" ? null : layoutVariant);
  };
  const data = layoutVariants.map(({ displayName, layoutVariant }) => ({
    label: displayName,
    value: layoutVariant || "default"
  }));
  return /* @__PURE__ */ React.createElement(SwitcherContainer, null, /* @__PURE__ */ React.createElement(SegmentedControl, { data, value: activeLayoutVariant || "default", onChange }));
};
const showBadge = ({ metrics }) => {
  var _a, _b;
  const sourceMetricValue = (_a = metrics == null ? void 0 : metrics[1]) == null ? void 0 : _a.value;
  const value = metrics && ((_b = metrics[0]) == null ? void 0 : _b.value);
  return Number.isInteger(sourceMetricValue) && Number.isInteger(value) && value !== 0 && sourceMetricValue !== 0;
};
const AlertsEntityTableTopSectionContent = ({
  expanded: initialExpanded,
  predefinedLayoutVariants,
  onExpandToggle
}) => {
  const [expanded, setExpanded] = useState(initialExpanded);
  const expandTitle = `${expanded ? "Collapse" : "Expand"} layout variant metrics`;
  const expandIcon = expanded ? "unfold_less" : "unfold_more";
  const onToggle = useCallback(
    () => setExpanded((_expanded) => {
      const newValue = !_expanded;
      onExpandToggle(newValue);
      return newValue;
    }),
    [onExpandToggle]
  );
  const layoutVariants = useMemo(
    () => [
      {
        displayName: "All",
        layoutVariant: null,
        metrics: null
      },
      ...predefinedLayoutVariants
    ],
    [predefinedLayoutVariants]
  );
  return /* @__PURE__ */ React.createElement(SectionContainer, null, /* @__PURE__ */ React.createElement(ContentRow, null, /* @__PURE__ */ React.createElement(Content, null, expanded ? /* @__PURE__ */ React.createElement(Container, null, layoutVariants.map((layoutVariant) => {
    var _a, _b;
    const { displayName, layoutVariant: predefinedLayoutVariant, metrics } = layoutVariant;
    const value = metrics && ((_a = metrics[0]) == null ? void 0 : _a.value);
    const meaning = metrics && ((_b = metrics[1]) == null ? void 0 : _b.meaning);
    const badgeValue = showBadge(layoutVariant) ? metrics[1].value : null;
    return /* @__PURE__ */ React.createElement(
      PredefinedLayoutItem,
      {
        key: predefinedLayoutVariant,
        title: displayName,
        count: value,
        badgeMeaning: meaning,
        badgeValue,
        layoutVariant: predefinedLayoutVariant,
        badgeStyle: "danger",
        description: layoutVariantDescriptions(predefinedLayoutVariant)
      }
    );
  })) : /* @__PURE__ */ React.createElement(AlertsEntityTableLayoutSwitcher, { layoutVariants })), /* @__PURE__ */ React.createElement(ToggleContainer, null, /* @__PURE__ */ React.createElement(IconButton, { title: expandTitle, name: expandIcon, onClick: onToggle }))));
};
const AlertsEntityTableTopSection = () => {
  var _a, _b;
  const { entityTableId } = useTableFetchContext();
  const { activeLayoutVariant } = useLayoutVariant();
  const sendTelemetry = useSendTelemetry("security-events");
  const { data: userLayoutPreferences, isInitialLoading: isLoadingPreferences } = useUserLayoutPreferences(entityTableId, activeLayoutVariant);
  const { mutateAsync: updatePreferences } = useUpdateUserLayoutPreferences(
    entityTableId,
    activeLayoutVariant
  );
  const { data, isFetching } = useLayoutVariants({
    entityListId: SECURITY_EVENTS_ENTITY_TABLE_ID,
    timerange: defaultTimeRange
  });
  const onExpandToggle = useCallback(
    (expanded) => {
      sendTelemetry(TELEMETRY_EVENT_TYPE.SECURITY_APP.SECURITY_EVENTS.LAYOUT_VARIANT_METRICS_TOGGLED, {
        event_details: { expanded }
      });
      return updatePreferences({
        customPreferences: {
          ...userLayoutPreferences.customPreferences,
          showLayoutVariantMetric: expanded
        }
      });
    },
    [sendTelemetry, updatePreferences, userLayoutPreferences.customPreferences]
  );
  if (isFetching || isLoadingPreferences) return /* @__PURE__ */ React.createElement(Spinner, null);
  return /* @__PURE__ */ React.createElement(
    AlertsEntityTableTopSectionContent,
    {
      expanded: (_b = (_a = userLayoutPreferences.customPreferences) == null ? void 0 : _a.showLayoutVariantMetric) != null ? _b : true,
      predefinedLayoutVariants: data,
      onExpandToggle
    }
  );
};
export default AlertsEntityTableTopSection;
